<template>
  <div class="relative">
    <!-- Loading Overlay -->
    <div v-if="loading" class="absolute inset-0 bg-gray-50 bg-opacity-75 flex items-center justify-center rounded z-50">
      <div class="text-center">
        <i class="fa fa-spinner fa-spin fa-3x text-primary"></i>
        <p class="mt-2 text-sm text-gray-600">Loading...</p>
      </div>
    </div>

    <form @submit.prevent="saveComplaint">
      <div class="grid grid-cols-12 gap-4">
        <label class="">Practitioner Number<i v-if="!formSubmitted" style="font-size: 10px" class="fas fa-asterisk text-red"></i></label>
        <div class="col-span-12">
          <div class="flex mb-2">
            <input type="text"
                   class="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue focus:border-transparent form-text noFocus my-0 border-r-0"
                   :class="practitioner || practitionerNumber !== null ? 'focus:border-green border-green':'focus:border-red border-red'"
                   :readonly="practitionerNumber !== null"
                   required
                   @input="searchPractitionerUpn"
                   v-model.trim="upn" />
            <div class="inline-flex items-center px-3 text-sm text-gray-600 bg-white border border-l-0 border-gray-300 rounded-r-md"
                 :class="practitioner || practitionerNumber !== null ? 'border-green':'border-red'">
              <i v-if="searchingPrn" style="font-size: 24px;" class="text-red fa fa-spinner fa-spin fa-fw"></i>
              <i v-if="!searchingPrn && !practitioner && practitionerNumber === null" style="font-size: 24px;" class="text-red fa fa-exclamation-triangle fa-fw"></i>
              <i v-if="(!searchingPrn && practitioner) || practitionerNumber !== null" style="font-size: 24px;" class="text-green fa fa-check fa-fw"></i>
            </div>
          </div>
          <span v-if="practitionerError !== null" class="bg-red px-2 py-1 text-xs rounded-full text-white">{{ practitionerError }}</span>
        </div>
      </div>
      <div class="grid grid-cols-12 gap-4 mb-2" v-if="practitionerAvatarUrl !== null || practitionerNumber === null">
        <div class="col-span-12 flex items-center">
          <img :src="practitionerAvatarUrl ?? undefined" alt="Practitioner Avatar"
               class="w-12 h-12 rounded-full object-cover mr-3" />
          <span class="mr-auto">{{ practitionerFullName }}</span>
        </div>
      </div>
      <div class="grid grid-cols-12 gap-4 mb-2">
        <div class="col-span-12">
          <label class="">Complaint<i v-if="!formSubmitted" style="font-size: 10px" class="fas fa-asterisk text-red"></i></label>
          <div class="flex flex-col gap-2 mb-2" id="input-group-1">
            <input type="text"
                   class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue focus:border-transparent form-text noFocus"
                   :class="form.complaint === null || form.complaint.length < 5 ? 'is-invalid':''"
                   id="reason"
                   v-model.trim="form.complaint"
                   :required="!formSubmitted">
            </input>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-12 gap-4 mb-2">
        <div class="col-span-12">
          <label class="">
            Description
            <i v-if="!formSubmitted" style="font-size: 10px" class="fas fa-asterisk text-red"></i>
          </label>
          <textarea
              @input="countChars()"
              id="textarea"
              v-model="form.descriptionOfComplaint"
              placeholder="Enter description..."
              rows="3"
              :class="[form.descriptionOfComplaint !== null && (form.descriptionOfComplaint.length >= 10 && form.descriptionOfComplaint.length <= 200) ? '' : 'is-invalid' ,'noFocus']"
              :required="!formSubmitted"
          ></textarea>
          <div v-if="form.descriptionOfComplaint !== null" class="w-full flex flex-row justify-between">
            <span v-if="form.descriptionOfComplaint.length < min" class="float-right text-xs text-red">Description needs to be at least {{ min }} characters</span>
            <span v-if="form.descriptionOfComplaint.length >= min" class="float-right text-xs text-red">{{ remaining }} characters remaining for the description</span>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-12 gap-4 mt-5">
        <div class="col-span-12">
          <button type="button" @click="hideComplaintForm" class="btn-primary w-100 block border-2 hover:bg-gray hover:text-white border-gray rounded-sm text-gray px-2 py-1">
            <i class="fa fa-times"></i> Cancel
          </button>
        </div>
        <div class="col-span-12">
          <button type="submit" class="btn-primary w-100 block border-2 hover:bg-green-dark text-white bg-green rounded-sm px-2 py-1">
            <i class="fa fa-check-circle"></i> Save Complaint
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { usePractitionersRegistryStore } from "@/stores/practitioners-registry";

const props = defineProps({
  practitionerNumber: {
    type: String,
    default: null
  }
});

const emit = defineEmits(["close"]);

const store = usePractitionersRegistryStore();

const upn = ref<string | null>(null);
const min = ref(10);
const max = ref(200);
const loading = ref(false);
const searchingPrn = ref(false);
const formSubmitted = ref(false);
const practitionerProfile = ref<any>(null);
const practitioner = ref(false);
const practitionerError = ref<string | null>(null);
const form = ref({
  complaint: null as string | null,
  complainantName: null as string | null,
  descriptionOfComplaint: null as string | null,
  uniquePractitionerNumber: null as string | null
});

onMounted(() => {
  let userProfile = JSON.parse(sessionStorage.getItem("current_user") || "{}");
  form.value.complainantName = `${userProfile.surName.toUpperCase()} ${userProfile.givenName.toUpperCase()}`;
  if (props.practitionerNumber !== null) {
    form.value.uniquePractitionerNumber = props.practitionerNumber;
    upn.value = props.practitionerNumber;
  }
});

const practitionerAvatarUrl = computed(() => {
  if (practitionerProfile.value === null) {
    return null;
  }
  if (!practitionerProfile.value.form1s.length) {
    return null;
  }

  const baseUrl = import.meta.env.VITE_API_BASE_URL + "/insolvency/api/practitioner-registration/passport-photo/";
  const form1 = practitionerProfile.value.form1s.find((f: any) => {
    return f.passportPhotos.length > 0;
  });

  if (form1 === undefined) {
    return null;
  }

  return baseUrl + form1.passportPhotos[0].filename;
});

const practitionerFullName = computed(() => {
  if (practitionerProfile.value === null) {
    return null;
  }

  let name = "";
  name += practitionerProfile.value.lastName.toUpperCase();
  name += " ";
  name += practitionerProfile.value.firstName.toUpperCase();
  return name;
});

const remaining = computed(() => {
  return form.value.descriptionOfComplaint === null ? max.value : max.value - form.value.descriptionOfComplaint.length;
});

const saveComplaint = () => {
  loading.value = false;
  if (form.value.complainantName === null) {
    console.error("complainant name missing");
  } else if (form.value.descriptionOfComplaint === null) {
    console.error("description name missing");
  } else if (form.value.complaint === null) {
    console.error("complaint missing");
  } else if (form.value.uniquePractitionerNumber === null) {
    console.error("practitioner number missing");
  } else {
    loading.value = true;
    store.logPractitionerComplaint(form.value);
  }
};

const searchPractitionerUpn = () => {
  practitionerError.value = null;
  practitionerProfile.value = null;
  if (upn.value !== null && upn.value.length) {
    searchingPrn.value = true;
    store.fetchPractitionerDetails(upn.value);
  }
};

const countChars = () => {
  if (form.value.descriptionOfComplaint && form.value.descriptionOfComplaint.length > max.value) {
    form.value.descriptionOfComplaint = form.value.descriptionOfComplaint.substr(0, max.value);
  }
};

const hideComplaintForm = () => {
  emit("close");
  form.value = {
    complaint: null,
    complainantName: null,
    descriptionOfComplaint: null,
    uniquePractitionerNumber: props.practitionerNumber
  };
};

watch(() => store.practitioner_details, (data) => {
  if (data) {
    practitioner.value = true;
    practitionerProfile.value = data;
    form.value.uniquePractitionerNumber = upn.value;
    searchingPrn.value = false;
  }
});

watch(() => store.error, (error) => {
  if (error) {
    practitionerError.value = error.message;
    form.value.uniquePractitionerNumber = null;
    searchingPrn.value = false;
  }
});

watch(() => store.practitioner_complaint, () => {
  loading.value = false;
  const query = {
    page: 1,
    perPage: 20,
    status: props.practitionerNumber === null ? "PENDING" : null,
    practitionerNumber: props.practitionerNumber
  };
  store.fetchPractitionerComplaints(query);
  hideComplaintForm();
});

watch(() => store.practitioner_complaint_error, (response) => {
  console.log(response);
  loading.value = false;
});
</script>
